import { useCallback, useContext, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { AdyenComponentContainer } from '@/components/expressPayment/AdyenComponentContainer';
import { useAdyenGooglePay } from '@/components/Wallets/AdyenGooglePay/useAdyenGooglePay';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useIsWalletZipTaxActive } from '@/hooks/useIsWalletZipTaxActive';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { useTranslate } from '@/hooks/useTranslate';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { GooglePayButtonPlaceholder } from '@/components/Wallets/WalletButton/GooglePayButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { EXPRESS_WALLET_NAME, WALLET_AVAILABILITY_REASON } from '@/utils/adyenWalletAvailability';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';

interface GooglePayAdyenFormProps extends Pick<ExpressCheckoutComponentProps, 'isExpress'> {
  onInvalidTaxClick?: () => void;
}

export const GooglePayAdyenForm = ({ onInvalidTaxClick, isExpress }: GooglePayAdyenFormProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    customer,
    payment: { isFormValid },
  } = context;
  // When the wallet-tax pipeline is on there's no on-page ZIP to validate.
  // Behind a ref so the mount-time handler closure always reads the current
  // value without re-creating the Adyen component.
  const isWalletZipTaxActive = useIsWalletZipTaxActive();
  const isWalletZipTaxActiveRef = useRef(isWalletZipTaxActive);
  isWalletZipTaxActiveRef.current = isWalletZipTaxActive;
  const { t } = useTranslate();
  const isFormValidRef = useRef(isFormValid);
  isFormValidRef.current = isFormValid;
  const [state, setState] = useState<WalletReadyState>('loading');
  const mountTimeRef = useRef(Date.now());
  // Bridges Adyen's `isAvailable` signal to the active template so the
  // FTD / last-method PREFERRED_PAYMENT_METHOD branches can swap to
  // DEFAULT when Google Pay is configured but the device can't show it.
  // Without this, the template was stranding the user on an empty
  // primary CTA. Standalone AdyenGooglePay already does this; the
  // `<Payment />`-mounted form variant was the missing link.
  const reportWalletAvailability = useExpressWalletFallback();

  const onClick = useCallback(
    (resolve: () => void, reject: () => void) => {
      if (isWalletZipTaxActiveRef.current || isFormValidRef.current) {
        trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });
        resolve();
      } else {
        if (onInvalidTaxClick) {
          onInvalidTaxClick();
        } else {
          const errorMessageKey = getZipCodeErrorMessageKey(customer);
          setError(t(errorMessageKey));
        }
        reject();
      }
    },
    [context, customer, onInvalidTaxClick, setError, t]
  );

  const handleReady = useCallback(
    (ready?: boolean, reason?: string) => {
      const isAvailable = ready === true;
      reportWalletAvailability(isAvailable, {
        wallet: EXPRESS_WALLET_NAME.GOOGLE_PAY,
        provider: ProviderEnum.ADYEN,
        reason: isAvailable ? undefined : (reason ?? WALLET_AVAILABILITY_REASON.UNKNOWN),
        loadTimeMs: Date.now() - mountTimeRef.current,
      });
      setState(isAvailable ? 'ready' : 'unavailable');
    },
    [reportWalletAvailability]
  );

  const { googlePayElement } = useAdyenGooglePay({
    onClick,
    onReady: handleReady,
  });

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported isExpress={isExpress} />;
  }

  return (
    <WalletButtonShell
      state={state}
      placeholder={<GooglePayButtonPlaceholder />}
      testId="wallet-btn-google-tile"
      usePlaceholderAsVisual
    >
      <AdyenComponentContainer element={googlePayElement} />
    </WalletButtonShell>
  );
};
